import { WarningIcon, CalendarDotsIcon, ClockIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { PageHeader } from 'components/PageHeader';
import { InvoiceStatus, OrderLineStopType } from 'src/types/enums';
import {
  approveInvoice,
  deleteInvoice,
  generateInvoice,
  isInvoiceListKey,
  markSalesInvoiceAsPaid,
  refreshInvoicePeppolStatus,
  removeOrdersFromInvoice,
  sendInvoiceViaPeppol,
  updateInvoice,
  useInvoice,
} from '../../models';
import type { InvoiceDetail } from '../../endpoints/InvoiceEndpoint.types';
import { InternalRole } from '../../../auth/endpoints/MeEndpoint.types';
import React, { useCallback, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import { invariant, nullthrows } from '@utils/invariant';
import classNames from '@utils/classnames';
import { FieldArray } from 'formik';
import { Formik } from 'components/form/formik';
import * as Yup from 'yup';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../../utils/track-event';
import { runPdfJobAndRefresh } from '../../../../utils/pdf-job-client';

import { Breadcrumb } from '../../../../components/Breadcrumb';
import { DataField } from '../../../../components/DataField';
import { ConfirmDialog } from '../../../../components/dialog/ConfirmDialog';
import { SendInvoiceDialog } from './SendInvoiceDialog';
import { formatDate, formatInputDate, formatInputTime } from '../../../../utils/date';
import { getDisplayError } from '../../../../utils/get-display-error';
import { decimateAndFormatNumber, parseNumberInput } from '../../../../utils/number';
import { FileUploaderButton } from '../../../document/components/FileUploaderButton';
import { getInvoiceTotals } from '../../../../domain/invoice-totals';
import { Pill } from '../../../../components/pill/Pill';
import { InvoiceStatusToColor, InvoiceStatusToText } from '../../utils/status';
import { FormDialog } from '../../../../components/dialog/FormDialog';
import { InputField } from '../../../../components/input/InputField';
import { useAuth } from '../../../../contexts/auth-context';
import { AddOrdersToInvoiceDialog } from '../../components/OrderSelectDialog';
import { Button } from '../../../../components/button/Button';
import { PageHeading } from 'components/PageHeading';
import { roleToNumber } from 'src/app/auth/roles.client';
import { VatRateComboboxField, vatRateToComboboxItem } from '../../../order/vatRate/VatRateComboboxField';
import { useMinimalGeneralSettings } from '../../../../contexts/minimal-settings-context';
import { getInitialLineValues } from './create-manual/CreateManualSaleInvoice';
import { ErrorStateBoundary } from '../../../../components/error-state/ErrorStateBoundary';
import { ErrorState } from 'components/error-state/ErrorState';
import { DelayedSpinner } from '../../../../components/Spinner';
import { Card } from '../../../../components/Card';
import { SummaryBar } from '../../../../components/SummaryBar';
import { TotalsCard } from '../../../../components/TotalsCard';
import { PeppolStatusCard } from '../../../peppol/components/PeppolStatusCard';
import { AuditLogsList } from '../../../audit-log/components/AuditLogsList';

const updateInvoiceSchema = Yup.object().shape({
  invoiceDate: Yup.string().min(1, 'Vereist').required('Vereist'),
  customerRef: Yup.string(),
});

export type Invoice = InvoiceDetail;

const InvoicePage = () => {
  const { me } = useAuth();
  const { settings } = useMinimalGeneralSettings();
  const navigate = useNavigate();
  const { mutate: globalMutate } = useSWRConfig();
  const { saleId } = useParams<{ saleId: string }>();
  invariant(saleId);
  const { data, error, isLoading, mutate } = useInvoice(saleId);

  // Bumped on every refresh so the history list reloads, also when no invoice field changed (e.g. an email re-send).
  const [historyVersion, setHistoryVersion] = useState(0);
  const refreshData = useCallback(() => {
    setHistoryVersion((v) => v + 1);
    return mutate();
  }, [mutate]);

  if (isLoading) {
    return <DelayedSpinner size={8} />;
  }

  if (error || !data) {
    return (
      <ErrorState
        error={error}
        title={error ? 'Factuur kon niet worden geladen' : 'Factuur niet gevonden'}
        description={error ? getDisplayError(error) : 'De gevraagde factuur werd niet gevonden.'}
        onRetry={error ? refreshData : undefined}
        variant="page"
      />
    );
  }

  const invoice = data;
  const totals = getInvoiceTotals([
    ...invoice.orders
      .map((o) =>
        o.lines
          .map((l) =>
            l.sales.map((s) => {
              return {
                ...s,
                vatRateId: s.vatRate.id,
              };
            }),
          )
          .flat(),
      )
      .flat(),
    ...invoice.manualInvoiceLines.map((v) => {
      return {
        ...v,
        vatRateId: v.vatRate.id,
      };
    }),
  ]);

  const isAdmin = roleToNumber(me.role) >= roleToNumber(InternalRole.InternalAdmin);
  const totalsMismatch =
    invoice.status !== InvoiceStatus.Draft &&
    (invoice.totalExclVat !== totals.totalExclVat || invoice.totalInclVat !== totals.totalInclVat);
  const title = `Factuur ${invoice.invoiceNumber ?? 'DRAFT'}`;
  return (
    <>
      <PageHeader title={title} />

      <PageHeading
        leftSide={
          <Breadcrumb
            parentItem={{
              name: 'Verkopen',
              to: '/internal/finance/sales',
            }}
            currentItem={title}
          />
        }
        rightSide={
          <div className="flex gap-2">
            {invoice.status === InvoiceStatus.Draft && (
              <FormDialog
                triggerText="Factuur goedkeuren"
                title="Ben je zeker dat je de factuur wilt goedkeuren?"
                submitText="Factuur goedkeuren"
                initialValues={{ invoiceNumber: '' }}
                onSubmit={async (values) => {
                  const invoiceNumber = values.invoiceNumber.trim();

                  try {
                    await approveInvoice(invoice.id, invoiceNumber ? +invoiceNumber : undefined);
                    trackEvent('sale_invoice_approved', {
                      invoice_id: invoice.id,
                      manual_invoice_number: Boolean(invoiceNumber),
                    });
                    refreshData();
                    toast.success('Factuur goedgekeurd');
                  } catch (err) {
                    captureException(err);
                    toast.error(`Kon factuur niet goedkeuren: ${getDisplayError(err)}`);
                  }
                }}
              >
                <div>Ben je zeker dat je de factuur wilt goedkeuren?</div>

                <div>
                  Vul het factuur nummer alleen in als je manueel een ander factuurnummer wilt gebruiken (uit het
                  verleden), anders zal het de automatische nummering de mist in laten gaan.
                </div>

                <InputField labelText="Factuur nummer" name="invoiceNumber" type="number" />
              </FormDialog>
            )}

            {invoice.status !== InvoiceStatus.Draft && invoice.status !== InvoiceStatus.Paid && (
              <FormDialog
                triggerText="Markeer als betaald"
                title="Ben je zeker dat je de factuur wilt markeren als betaald?"
                submitText="Markeer als betaald"
                initialValues={{ paidAt: formatInputDate(new Date()) }}
                onSubmit={async (values) => {
                  const paidAt = values.paidAt;

                  try {
                    await markSalesInvoiceAsPaid(invoice.id, paidAt);
                    trackEvent('sale_invoice_marked_paid', {
                      invoice_id: invoice.id,
                      paid_at: paidAt,
                    });
                    toast.success('Factuur gemarkeerd als betaald');
                  } catch (err) {
                    captureException(err);
                    toast.error(`Kon factuur niet markeren als betaald: ${getDisplayError(err)}`);
                  }
                }}
              >
                <InputField labelText="Betaald op" name="paidAt" type="date" />
              </FormDialog>
            )}

            {invoice.status === InvoiceStatus.Draft && <AddOrdersToInvoiceDialog invoice={invoice} />}

            {invoice.status !== InvoiceStatus.Draft && invoice.status !== InvoiceStatus.Paid && (
              <SendInvoiceDialog
                invoice={invoice}
                onSent={async () => {
                  await refreshData();
                }}
              />
            )}

            <div>
              <FileUploaderButton
                buttonText="VKF"
                title="Verkoopfactuur"
                file={invoice.document}
                generateDocument={async () => {
                  await runPdfJobAndRefresh(await generateInvoice(invoice.id), async () => {
                    await refreshData();
                  });
                  trackEvent('sale_invoice_generated', { invoice_id: invoice.id });
                }}
                disableUpload
              />
            </div>

            {isAdmin && (
              <ConfirmDialog
                triggerColor="danger"
                triggerText={<TrashIcon className="button-icon" />}
                title="Factuur verwijderen?"
                submitText="Verwijder factuur"
                description={
                  <div>
                    Ben je zeker dat je deze factuur wilt verwijderen? Dit kan ervoor zorgen dat de facturen niet meer
                    opeenlopend zijn qua nummering.
                  </div>
                }
                onSubmit={async () => {
                  try {
                    await deleteInvoice(invoice.id);
                    await globalMutate(isInvoiceListKey);
                    trackEvent('sale_invoice_deleted', { invoice_id: invoice.id });
                    toast.success('Factuur verwijderd');
                    navigate('..');
                  } catch (err) {
                    captureException(err);
                    toast.error(`Kon factuur niet verwijderen: ${getDisplayError(err)}`);
                  }
                }}
              />
            )}
          </div>
        }
      />

      <div className="flex flex-col gap-4 p-7">
        {totalsMismatch && (
          <div className="warning-bar">
            Totaal op de factuur komt niet overeen met het totaal van de orders, dit wil zeggen dat er na het goedkeuren
            van de factuur nog orders aangepast geweest zijn.
          </div>
        )}

        <SummaryBar>
          <DataField title="Klant">
            {invoice.customer.name} ({invoice.customer.accountingId ?? invoice.customer.id})
          </DataField>
          <DataField title="Gestructureerde mededeling">{invoice.structuredComment}</DataField>
          <DataField title="Status">
            <Pill color={InvoiceStatusToColor[invoice.status]} size="sm">
              {InvoiceStatusToText[invoice.status]}
            </Pill>
          </DataField>
          <DataField title="Factuur datum">{formatDate(invoice.date)}</DataField>
          {!!invoice.customerRef && <DataField title="Referentie klant">{invoice.customerRef}</DataField>}
          {invoice.sentAt && <DataField title="Verstuurd op">{formatDate(invoice.sentAt)}</DataField>}
          {invoice.sentAt && <DataField title="Vervalt op">{formatDate(invoice.expiresAt)}</DataField>}
          {invoice.paidAt && <DataField title="Betaald op">{formatDate(invoice.paidAt)}</DataField>}
          {invoice.status !== InvoiceStatus.Draft && (
            <DataField title="Totaal excl btw">
              <div
                className={classNames('whitespace-nowrap flex items-center gap-2', {
                  'text-negative-text': totalsMismatch,
                })}
              >
                {totalsMismatch && <WarningIcon className="w-4 h-4" />}
                {`€ ${decimateAndFormatNumber(totals.totalExclVat, 2, {
                  decimalSeperator: ',',
                })}`}
              </div>
            </DataField>
          )}
          {invoice.status !== InvoiceStatus.Draft && (
            <DataField title="Totaal incl btw">
              <div
                className={classNames('whitespace-nowrap flex items-center gap-2', {
                  'text-negative-text': totalsMismatch,
                })}
              >
                {totalsMismatch && <WarningIcon className="w-4 h-4" />}
                {`€ ${decimateAndFormatNumber(totals.totalInclVat, 2, {
                  decimalSeperator: ',',
                })}`}
              </div>
            </DataField>
          )}
        </SummaryBar>

        {invoice.status !== InvoiceStatus.Draft && (
          <PeppolStatusCard
            peppol={invoice.peppol}
            blockReason={invoice.peppolAvailability}
            onSend={() => sendInvoiceViaPeppol(invoice.id)}
            onRefresh={() => refreshInvoicePeppolStatus(invoice.id)}
            onChanged={refreshData}
          />
        )}

        {invoice.status === InvoiceStatus.Draft && (
          <Card title="Factuurgegevens">
            <Formik
              initialValues={{
                customerRef: invoice.customerRef ?? '',
                invoiceDate: formatInputDate(invoice.date ?? new Date()),
                manualLines: invoice.manualInvoiceLines.map((line) => ({
                  description: line.description,
                  amount: decimateAndFormatNumber(line.amount, 2),
                  unitPrice: decimateAndFormatNumber(line.unitPrice, 2),
                  vatRate: vatRateToComboboxItem(line.vatRate),
                })),
              }}
              validationSchema={updateInvoiceSchema}
              onSubmit={async (values) => {
                try {
                  const initialManualLines = invoice.manualInvoiceLines.map((line) => ({
                    description: line.description,
                    amount: decimateAndFormatNumber(line.amount, 2),
                    unitPrice: decimateAndFormatNumber(line.unitPrice, 2),
                    vatRate: vatRateToComboboxItem(line.vatRate),
                  }));
                  const manualLinesChanged = JSON.stringify(values.manualLines) !== JSON.stringify(initialManualLines);
                  await updateInvoice(invoice.id, {
                    date: values.invoiceDate,
                    customerRef: values.customerRef,
                    manualInvoiceLines: manualLinesChanged
                      ? values.manualLines.map((line) => ({
                          description: line.description,
                          amount: parseNumberInput(line.amount, 2),
                          unitPrice: parseNumberInput(line.unitPrice, 2),
                          vatRateId: nullthrows(line.vatRate, 'BTW tarief niet geselecteerd').key,
                        }))
                      : undefined,
                  });
                  refreshData();
                  toast.success('Factuur aangepast');
                } catch (err) {
                  captureException(err);
                  console.error(err);
                  toast.error('Kon factuur niet aanpassen: ' + getDisplayError(err));
                }
              }}
            >
              {({ handleSubmit, isSubmitting, dirty, values }) => {
                return (
                  <form onSubmit={handleSubmit}>
                    <div className="flex gap-4">
                      <InputField labelText="Factuurdatum" type="date" name="invoiceDate" />
                      <InputField labelText="Ref klant" type="text" name="customerRef" />
                    </div>

                    <FieldArray
                      name="manualLines"
                      render={(arrayHelpers) => (
                        <div className="my-4">
                          <div className="flex justify-between items-end">
                            <div className="heading-two mb-2">Manuele lijnen</div>
                            <Button
                              onTrigger={() => {
                                arrayHelpers.push({
                                  ...getInitialLineValues(settings, invoice.customer),
                                });
                              }}
                              iconLeft={<PlusIcon className="button-icon" />}
                            >
                              Voeg lijn toe
                            </Button>
                          </div>

                          {values.manualLines.length > 0 ? (
                            <div>
                              {values.manualLines.map((_line, index) => {
                                return (
                                  <ErrorStateBoundary key={`manual-line-${index}`} variant="inline">
                                    <div className="mt-4">
                                      <div className="flex gap-2 items-center">
                                        <div className="flex-1">
                                          <InputField
                                            labelText="Beschrijving"
                                            type="text"
                                            name={`manualLines[${index}].description`}
                                          />
                                        </div>

                                        <Button
                                          color="danger"
                                          onTrigger={() => {
                                            arrayHelpers.remove(index);
                                          }}
                                          iconLeft={<TrashIcon className="button-icon" />}
                                        >
                                          Verwijder
                                        </Button>
                                      </div>

                                      <div className="flex gap-2">
                                        <div className="flex-2">
                                          <InputField
                                            labelText="Aantal"
                                            type="number"
                                            name={`manualLines[${index}].amount`}
                                            step="0.01"
                                          />
                                        </div>
                                        <div className="flex-2">
                                          <InputField
                                            labelText="Eenheidsprijs"
                                            type="number"
                                            name={`manualLines[${index}].unitPrice`}
                                            step="0.01"
                                          />
                                        </div>
                                        <div className="flex-3">
                                          <VatRateComboboxField
                                            labelText="BTW Tarief"
                                            name={`manualLines[${index}].vatRate`}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </ErrorStateBoundary>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-fg-muted my-2">Geen manuele lijnen</div>
                          )}
                        </div>
                      )}
                    />

                    <div className="mt-4 mb-4">
                      <Button
                        type="submit"
                        color="primary"
                        isDisabled={!dirty || isSubmitting}
                        isLoading={isSubmitting}
                        iconLeft={<PlusIcon className="button-icon" />}
                      >
                        Pas aan
                      </Button>
                    </div>
                  </form>
                );
              }}
            </Formik>
          </Card>
        )}

        <>
          {invoice.orders
            .sort((a, b) => a.customerRef.localeCompare(b.customerRef))
            .map((order) => {
              const orderTotals = getInvoiceTotals(
                order.lines
                  .map((l) =>
                    l.sales.map((s) => {
                      return {
                        ...s,
                        vatRateId: s.vatRate.id,
                      };
                    }),
                  )
                  .flat(),
              );

              return (
                <div className="card" key={`order-${order.orderNumber}`}>
                  <div className="card-heading">
                    <Link to={`/internal/orders/${order.id}/general`} className="link-text">
                      Order {order.orderNumber} - {order.customerRef}
                    </Link>

                    <div>
                      {invoice.status === InvoiceStatus.Draft && (
                        <ConfirmDialog
                          triggerColor="danger"
                          triggerText={<TrashIcon className="button-icon" />}
                          title={`Ben je zeker dat je order ${order.orderNumber} uit de factuur wilt verwijderen?`}
                          submitText="Verwijder order uit factuur"
                          description={
                            <div>Ben je zeker dat je order {order.orderNumber} uit de factuur wilt verwijderen?</div>
                          }
                          onSubmit={async () => {
                            try {
                              await removeOrdersFromInvoice(invoice.id, [order.id]);
                              refreshData();
                              await globalMutate(isInvoiceListKey);
                              toast.success('Order uit factuur verwijderd');
                            } catch (err) {
                              captureException(err);
                              toast.error(`Kon order niet uit de factuur verwijderen: ${getDisplayError(err)}`);
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>

                  <div className="card-content flex flex-col gap-3.5">
                    {order.lines.map((line) => {
                      return (
                        <div className="flex flex-col gap-3.5" key={`line-${line.id}`}>
                          <div className="flex flex-col gap-2">
                            {line.stops
                              .sort((a, b) => a.sequenceIndex - b.sequenceIndex)
                              .map((stop) => {
                                return (
                                  <div
                                    className="grid grid-cols-4 items-center gap-3 text-sm"
                                    key={`line-${line.id}-${stop.id}`}
                                  >
                                    <div className="font-semibold text-fg">
                                      {stop.type === OrderLineStopType.Load ? 'Laden' : 'Lossen'}
                                    </div>
                                    <div className="text-fg-secondary">
                                      {stop.location.country}-{stop.location.name}
                                    </div>

                                    <div className="flex items-center gap-1.5 text-fg-secondary">
                                      <CalendarDotsIcon className="h-3.5 w-3.5 text-fg-muted" />
                                      {formatDate(stop.date)}
                                    </div>
                                    <div className="flex items-center gap-1.5 text-fg-secondary">
                                      <ClockIcon className="h-3.5 w-3.5 text-fg-muted" />
                                      {`${formatInputTime(stop.timeStart)}-${formatInputTime(stop.timeEnd)}`}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>

                          <div className="overflow-hidden rounded-md border border-border">
                            <div className="grid grid-cols-5 gap-x-4 border-b border-border bg-surface-sunken px-3.5 py-2">
                              <div className="label-text">Beschrijving</div>
                              <div className="label-text">Aantal</div>
                              <div className="label-text">Eenheidsprijs</div>
                              <div className="label-text">Totaal</div>
                              <div className="label-text">Btw</div>
                            </div>

                            <div className="grid grid-cols-5 gap-x-4 gap-y-1 px-3.5 py-2.5 text-sm">
                              {line.sales.map((sale) => {
                                return (
                                  <React.Fragment key={`sale-${sale.id}`}>
                                    <div>{sale.productType.nameNl}</div>
                                    <div className="whitespace-nowrap">
                                      {decimateAndFormatNumber(sale.amount, 2, {
                                        decimalSeperator: ',',
                                      })}
                                    </div>
                                    <div className="whitespace-nowrap">
                                      €{' '}
                                      {decimateAndFormatNumber(sale.unitPrice, 2, {
                                        decimalSeperator: ',',
                                      })}
                                    </div>
                                    <div className="whitespace-nowrap">
                                      €{' '}
                                      {decimateAndFormatNumber((sale.amount * sale.unitPrice) / 100, 2, {
                                        decimalSeperator: ',',
                                      })}
                                    </div>
                                    <div
                                      className={classNames('whitespace-nowrap', {
                                        'text-negative-text': sale.vatRate.id !== invoice.customer.defaultVatRate.id,
                                      })}
                                    >
                                      €{' '}
                                      {decimateAndFormatNumber(
                                        (sale.amount * sale.unitPrice * sale.vatRatePercentage) / 1000000,
                                        2,
                                        {
                                          decimalSeperator: ',',
                                        },
                                      )}{' '}
                                      -{' '}
                                      {decimateAndFormatNumber(sale.vatRatePercentage, 2, {
                                        decimalSeperator: ',',
                                      })}
                                      %
                                    </div>

                                    {!!sale.externalNote && (
                                      <div className="col-span-5">
                                        <div className="text-sm">Extra informatie: {sale.externalNote}</div>
                                      </div>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    <div className="whitespace-nowrap text-xs text-fg-muted">
                      Order totaal: €{' '}
                      {decimateAndFormatNumber(orderTotals.totalExclVat, 2, {
                        decimalSeperator: ',',
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
        </>

        {invoice.manualInvoiceLines.length > 0 && (
          <Card title="Manuele lijnen" flush>
            <div className="grid grid-cols-5 gap-x-4 border-b border-border px-5 py-2">
              <div className="label-text">Beschrijving</div>
              <div className="label-text">Aantal</div>
              <div className="label-text">Eenheidsprijs</div>
              <div className="label-text">Totaal</div>
              <div className="label-text">Btw</div>
            </div>

            <div className="grid grid-cols-5 gap-x-4 gap-y-1 px-5 py-3 text-sm">
              {invoice.manualInvoiceLines.map((line) => {
                return (
                  <React.Fragment key={`manual-invoice-line-${line.id}`}>
                    <div>{line.description}</div>
                    <div className="whitespace-nowrap">
                      {decimateAndFormatNumber(line.amount, 2, {
                        decimalSeperator: ',',
                      })}
                    </div>
                    <div className="whitespace-nowrap">
                      €{' '}
                      {decimateAndFormatNumber(line.unitPrice, 2, {
                        decimalSeperator: ',',
                      })}
                    </div>
                    <div className="whitespace-nowrap">
                      €{' '}
                      {decimateAndFormatNumber((line.amount * line.unitPrice) / 100, 2, {
                        decimalSeperator: ',',
                      })}
                    </div>
                    <div
                      className={classNames('whitespace-nowrap', {
                        'text-negative-text': line.vatRate.id !== invoice.customer.defaultVatRate.id,
                      })}
                    >
                      €{' '}
                      {decimateAndFormatNumber((line.amount * line.unitPrice * line.vatRatePercentage) / 1000000, 2, {
                        decimalSeperator: ',',
                      })}{' '}
                      -{' '}
                      {decimateAndFormatNumber(line.vatRatePercentage, 2, {
                        decimalSeperator: ',',
                      })}
                      %
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </Card>
        )}

        <TotalsCard
          className={classNames({ 'text-negative-text': totalsMismatch })}
          rows={[
            {
              label: 'Totaal excl. btw:',
              value: (
                <span className="flex items-center gap-2">
                  {totalsMismatch && <WarningIcon className="h-4 w-4" />}
                  {`€ ${decimateAndFormatNumber(totals.totalExclVat, 2, {
                    decimalSeperator: ',',
                  })}`}
                </span>
              ),
            },
            {
              label: 'Totaal btw:',
              value: (
                <span className="flex items-center gap-2">
                  {totalsMismatch && <WarningIcon className="h-4 w-4" />}
                  {`€ ${decimateAndFormatNumber(totals.totalVat, 2, {
                    decimalSeperator: ',',
                  })}`}
                </span>
              ),
            },
            {
              label: 'Totaal incl. btw:',
              isEmphasised: true,
              value: (
                <span className="flex items-center gap-2">
                  {totalsMismatch && <WarningIcon className="h-4 w-4" />}
                  {`€ ${decimateAndFormatNumber(totals.totalInclVat, 2, {
                    decimalSeperator: ',',
                  })}`}
                </span>
              ),
            },
          ]}
        />

        <div>
          <h2 className="heading-two mb-2">Historiek</h2>
          <AuditLogsList key={invoice.id} filter={{ invoiceId: invoice.id }} refreshToken={historyVersion} />
        </div>
      </div>
    </>
  );
};

export default InvoicePage;
